import React, { useEffect, useRef, useCallback, useState, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../../contexts/ThemeContext';
import Icon from '../Icon';
import { splitFileName } from '../../utils/formatters';
import i18n from '../../i18n';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  z-index: 1001;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background-color: rgba(0, 0, 0, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h3`
  margin: 0;
  color: white;
  font-size: ${({ $layout }) => $layout.fontSize}px;
  font-weight: 500;
  display: flex;
  align-items: baseline;
  max-width: calc(100% - 60px);
  min-width: 0;
`;

const TitleBaseName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TitleExtension = styled.span`
  white-space: nowrap;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

const NotAvailable = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: ${({ $layout }) => $layout.fontSize}px;
  text-align: center;
`;

const FilePreviewModal = ({ attachment, onClose }) => {
  const { layout } = useContext(ThemeContext);
  const modalRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const urlRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // PDFs: open in new tab (Chrome blocks PDF viewer inside iframes)
  useEffect(() => {
    if (!attachment?.file) return undefined;

    if (attachment.file.type === 'application/pdf') {
      const url = URL.createObjectURL(attachment.file);
      window.open(url, '_blank');
      // Revoke after a short delay to let the new tab load
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      onClose();
      return undefined;
    }

    lastFocusedRef.current = document.activeElement;
    const url = URL.createObjectURL(attachment.file);
    urlRef.current = url;
    setPreviewUrl(url);

    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [attachment, onClose]);

  const handleClose = useCallback(() => {
    onClose();
    if (lastFocusedRef.current && typeof lastFocusedRef.current.focus === 'function') {
      lastFocusedRef.current.focus();
    }
  }, [onClose]);

  // Keyboard handling and focus trap
  useEffect(() => {
    if (!previewUrl) return undefined;

    const focusable = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusable && focusable.length > 0) {
      focusable[0].focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
      if (event.key === 'Tab' && focusable && focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewUrl, handleClose]);

  if (!previewUrl) return null;

  return (
    <Overlay
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-modal-title"
      onClick={handleClose}
    >
      <Header onClick={(e) => e.stopPropagation()}>
        <Title $layout={layout} id="preview-modal-title" title={attachment.filename}>
          <TitleBaseName>{splitFileName(attachment.filename).baseName}</TitleBaseName>
          {splitFileName(attachment.filename).extension && (
            <TitleExtension>{splitFileName(attachment.filename).extension}</TitleExtension>
          )}
        </Title>
        <CloseButton
          onClick={handleClose}
          aria-label={i18n('close') || 'Close'}
        >
          <Icon
            icon="close"
            iconLib="material"
            iconColor="white"
            iconSize={24}
          />
        </CloseButton>
      </Header>
      <Content onClick={(e) => e.stopPropagation()}>
        {attachment.file?.type?.startsWith('image/') ? (
          <PreviewImage
            src={previewUrl}
            alt={attachment.filename}
          />
        ) : (
          <NotAvailable $layout={layout}>
            {i18n('translation_order_preview_not_available')}
          </NotAvailable>
        )}
      </Content>
    </Overlay>
  );
};

FilePreviewModal.propTypes = {
  attachment: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FilePreviewModal;
